import { useCallback, useEffect, useRef, useState } from "react";
import { FixedSizeList as List } from "react-window";
import api from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const ROW_H = 44;
const PAGE = 100;

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const now = new Date();
  return d.getFullYear() === now.getFullYear()
    ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

// Traditional inbox list: newest first, cursor-paginated, windowed so only visible rows render.
export default function MessageList({ accountId, filter, version, picked, allMatching, onTogglePick, onPickLoaded, accountLabels }) {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [height, setHeight] = useState(480);
  const reqId = useRef(0);
  const loadingRef = useRef(false);

  useEffect(() => {
    const fit = () => setHeight(Math.max(300, window.innerHeight - 420));
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  const fetchPage = useCallback(
    async (after, id) => {
      if (loadingRef.current && after) return;
      loadingRef.current = true;
      setLoading(true);
      try {
        const { data } = await api.post(`/cleanup/accounts/${accountId}/messages`, {
          filter,
          cursor: after || undefined,
          limit: PAGE,
        });
        if (id !== reqId.current) return;
        setItems((cur) => (after ? [...cur, ...data.items] : data.items));
        setCursor(data.next_cursor);
        setHasMore(Boolean(data.next_cursor));
      } catch (e) {
        if (id === reqId.current) toast.error(e?.response?.data?.detail || "Failed to load messages");
      } finally {
        if (id === reqId.current) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [accountId, filter]
  );

  // Restart from the top whenever the account, filter, or refresh version changes.
  useEffect(() => {
    reqId.current += 1;
    loadingRef.current = false;
    setItems([]);
    setCursor(null);
    setHasMore(false);
    fetchPage(null, reqId.current);
  }, [fetchPage, version]);

  useEffect(() => {
    onPickLoaded?.(items.map((m) => m.email_id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const Row = ({ index, style }) => {
    const m = items[index];
    if (!m) {
      return (
        <div style={style} className="px-3 text-xs text-muted-foreground flex items-center">
          Loading…
        </div>
      );
    }
    const checked = allMatching || picked.has(m.email_id);
    return (
      <label
        style={style}
        className={`flex items-center gap-3 px-3 border-b border-border cursor-pointer hover:bg-secondary/40 ${checked ? "bg-primary/5" : ""}`}
        data-testid={`msg-row-${m.email_id}`}
      >
        <Checkbox checked={checked} disabled={allMatching} onCheckedChange={() => onTogglePick(m.email_id)} />
        <span className="w-32 sm:w-52 shrink-0 truncate text-sm font-medium">{m.sender_name || m.sender_email}</span>
        <span className="flex-1 min-w-0 truncate text-sm text-muted-foreground">{m.subject || "(no subject)"}</span>
        {accountLabels && (
          <span className="hidden md:inline shrink-0 max-w-[10rem] truncate text-xs font-mono text-muted-foreground border border-border px-1">
            {accountLabels[m.account_id] || m.account_id}
          </span>
        )}
        <span className="shrink-0 font-mono text-xs text-muted-foreground">{fmtDate(m.received_at)}</span>
      </label>
    );
  };

  if (!items.length && !loading) {
    return <p className="p-6 text-sm text-muted-foreground" data-testid="list-empty">No messages match.</p>;
  }

  return (
    <div data-testid="message-list">
      <List
        height={height}
        width="100%"
        itemCount={hasMore ? items.length + 1 : items.length}
        itemSize={ROW_H}
        onItemsRendered={({ visibleStopIndex }) => {
          if (hasMore && !loadingRef.current && visibleStopIndex >= items.length - 10) fetchPage(cursor, reqId.current);
        }}
      >
        {Row}
      </List>
    </div>
  );
}
